'use client';

import { useEffect, useRef } from 'react';
import AppImage from '@/components/ui/AppImage';

const instructors = [
{
  name: 'Ravi Sharma',
  role: 'Founder & Head Guru',
  exp: '18+ years',
  specialty: 'Classical Garba, Raas',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1e79874ce-1763295572482.png",
  alt: 'Male Garba instructor Ravi Sharma in traditional white kurta with warm smile',
  badge: 'Founder',
  students: '2,400+'
},
{
  name: 'Meera Patel',
  role: 'Senior Instructor',
  exp: '12 years',
  specialty: 'Dandiya Raas, Kids',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1dc0bd021-1768204919057.png",
  alt: 'Female Garba instructor Meera Patel in colorful chaniya choli teaching students',
  badge: 'Senior',
  students: '900+'
},
{
  name: 'Ankit Desai',
  role: 'Competition Coach',
  exp: '9 years',
  specialty: 'Competitive Garba',
  image: "https://images.unsplash.com/photo-1723637375828-a3ecfcc6636d",
  alt: 'Male competition Garba coach Ankit Desai in athletic traditional attire',
  badge: 'Champion',
  students: '600+'
}];


export default function InstructorsSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.reveal-hidden').forEach((el, i) => {
              setTimeout(() => el.classList.add('revealed'), i * 120);
            });
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef?.current) observer?.observe(sectionRef?.current);
    return () => observer?.disconnect();
  }, []);

  return (
    <section id="instructors" className="py-28 bg-bg-warm" ref={sectionRef}>
      <div className="max-w-7xl mx-auto px-5">
        <div className="text-center mb-16 reveal-hidden">
          <p className="text-primary font-bold uppercase tracking-[0.3em] text-[11px] mb-3">Meet the Gurus</p>
          <h2 className="font-display text-5xl md:text-6xl font-black text-foreground leading-tight">
            Expert Instructors,
            <br />
            <span className="font-display italic text-gradient-warm">Authentic Tradition.</span>
          </h2>
        </div>

        {/* Asymmetric bento grid */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          {/* Featured instructor — large card */}
          <div className="md:col-span-5 reveal-hidden instructor-card">
            <div className="bg-white rounded-4xl overflow-hidden shadow-warm-md border border-primary/8 h-full">
              <div className="relative aspect-[4/5] overflow-hidden">
                <AppImage
                  src={instructors?.[0]?.image}
                  alt={instructors?.[0]?.alt}
                  className="w-full h-full object-cover" />
                
                <div className="absolute inset-0 bg-gradient-to-t from-dark/70 via-dark/10 to-transparent" />
                <div className="absolute top-4 left-4">
                  <span className="bg-accent text-dark text-[10px] font-black uppercase tracking-widest px-3 py-1.5 rounded-full">
                    {instructors?.[0]?.badge}
                  </span>
                </div>
                <div className="absolute bottom-0 inset-x-0 p-6 text-white">
                  <h3 className="font-display text-3xl font-bold">{instructors?.[0]?.name}</h3>
                  <p className="text-white/80 text-sm mt-1">{instructors?.[0]?.role}</p>
                  <div className="flex gap-4 mt-3">
                    <span className="text-xs bg-white/15 backdrop-blur-sm rounded-full px-3 py-1">{instructors?.[0]?.exp}</span>
                    <span className="text-xs bg-white/15 backdrop-blur-sm rounded-full px-3 py-1">{instructors?.[0]?.students} students</span>
                  </div>
                </div>
              </div>
              <div className="p-6">
                <p className="text-muted text-sm"><span className="font-semibold text-foreground">Specialty:</span> {instructors?.[0]?.specialty}</p>
              </div>
            </div>
          </div>

          {/* Right column — two smaller cards + stat card */}
          <div className="md:col-span-7 grid grid-rows-[auto_auto_auto] gap-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {instructors?.slice(1)?.map((inst, i) =>
              <div key={inst?.name} className={`reveal-hidden instructor-card delay-${(i + 1) * 200}`}>
                  <div className="bg-white rounded-4xl overflow-hidden shadow-warm-md border border-primary/8 h-full">
                    <div className="relative aspect-[4/3] overflow-hidden">
                      <AppImage
                      src={inst?.image}
                      alt={inst?.alt}
                      className="w-full h-full object-cover" />
                    
                      <div className="absolute inset-0 bg-gradient-to-t from-dark/60 to-transparent" />
                      <div className="absolute top-3 left-3">
                        <span className="bg-primary text-white text-[10px] font-black uppercase tracking-widest px-3 py-1.5 rounded-full">
                          {inst?.badge}
                        </span>
                      </div>
                      <div className="absolute bottom-0 inset-x-0 p-4 text-white">
                        <h3 className="font-display text-xl font-bold">{inst?.name}</h3>
                        <p className="text-white/75 text-xs mt-0.5">{inst?.role}</p>
                      </div>
                    </div>
                    <div className="p-4">
                      <div className="flex justify-between text-xs text-muted">
                        <span>{inst?.exp} exp</span>
                        <span>{inst?.students} students</span>
                      </div>
                      <p className="text-muted text-xs mt-2"><span className="font-semibold text-foreground">Specialty:</span> {inst?.specialty}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Stats / achievements card */}
            <div className="reveal-hidden delay-300">
              <div className="bg-gradient-warm rounded-4xl p-8 text-white shadow-warm-lg">
                <p className="text-white/80 text-sm font-semibold uppercase tracking-widest mb-5">Our Achievements</p>
                <div className="grid grid-cols-3 gap-6">
                  {[
                  { num: '40+', label: 'Competition Wins' },
                  { num: '18', label: 'Years Running' },
                  { num: '12+', label: 'Expert Gurus' }]?.
                  map((a) =>
                  <div key={a?.label} className="text-center">
                      <p className="font-display text-3xl font-black">{a?.num}</p>
                      <p className="text-white/75 text-xs mt-1 font-medium">{a?.label}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>);

}